// Link file: components/editor/RichTextRenderer.tsx
// File này để làm gì: Hiển thị nội dung câu hỏi (HTML đã sanitize lúc ghi, hoặc text trơn kiểu cũ) + vẽ công thức KaTeX.
//   07/10/2026: bỏ bước giải mã &lt; → < toàn cục (lỗ XSS: chữ người sửa gõ "<img onerror>" đã escape bị giải mã thành thẻ thật).
// Ai dùng nó: QuestionCard, ReviewReaderCard, MindmapQuestionPopup, trang câu hỏi... (mọi nơi render nội dung câu hỏi)
// DB schema tác động: không (chỉ đọc questions.content / options[].text / explanation)
// Nó tác động file nào: không

"use client";

import { useMemo, useEffect, useState } from "react";
import katex from "katex";

/**
 * Strip HTML wrapper tags bên trong block LaTeX.
 * TipTap/Word khi paste công thức multi-line hay nhồi `<p>...</p>` vào giữa
 * khiến KaTeX không parse được. Hàm này clean body math trước khi render.
 *
 * Đặc biệt: <em>X</em> được convert lại thành _X_ vì TipTap parse markdown
 * _X_ thành italic, làm mất dấu _ subscript LaTeX.
 */
function cleanMathBody(body: string): string {
  return body
    // 1. </p><p>, <p>, </p>, <br> → newline (multi-line math)
    .replace(/<\/p\s*>\s*<p[^>]*>/gi, "\n")
    .replace(/<\/?p[^>]*>/gi, "\n")
    .replace(/<br\s*\/?>/gi, "\n")
    // 2. CRITICAL: <em>X</em> → _X_ — restore subscript LaTeX bị TipTap nuốt
    //    BẮT BUỘC chạy TRƯỚC strip generic ở bước 3.
    .replace(/<em[^>]*>([\s\S]*?)<\/em>/gi, "_$1_")
    // 3. Strip các tag inline còn lại (KHÔNG bao gồm em vì đã xử lý ở bước 2)
    .replace(/<\/?(?:span|div|strong|b|i|u|mark|small|sub|sup)[^>]*>/gi, "")
    // 4. Decode HTML entities
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&nbsp;/g, " ")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .trim();
}

const HAS_TAG = /<\/?[a-z][^>]*>/i;
// Thẻ an toàn KHÔNG thuộc tính bị lưu dạng escape ở dữ liệu cũ (site cũ / Excel) → chỉ những thẻ này được khôi phục
const SAFE_ESCAPED_TAG = /&lt;(\/?)(p|br|strong|b|em|i|u|s|sub|sup|ul|ol|li|h2|h3|blockquote)\s*\/?&gt;/gi;

/**
 * Pipeline xử lý chuỗi:
 * 1. Có thẻ HTML thật → giữ nguyên (đã sanitize lúc ghi). KHÔNG giải mã &lt; (chống XSS).
 *    Text trơn kiểu cũ → escape < >, khôi phục thẻ an toàn không thuộc tính, \n → <br/> (mẫu htmlCongThuc hubdocu).
 * 2. Parse KaTeX: $$...$$ (display) rồi $...$ (inline), \[...\], \(...\)
 *    — pre-clean body math khỏi HTML wrapper trước khi render
 */
function processRichText(raw: string): string {
  if (!raw) return "";

  // Bước 1
  let html = raw;
  if (!HAS_TAG.test(raw)) {
    html = raw.replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(SAFE_ESCAPED_TAG, "<$1$2>");
    if (!/<(p|br|ul|ol|li|h[1-6]|blockquote)[>\s/]/i.test(html)) html = html.replace(/(?:\r\n|\r|\n)/g, "<br />");
  }

  // Bước 2a: Parse $$...$$ (display mode) — multi-line OK
  html = html.replace(/\$\$([\s\S]+?)\$\$/g, (match, tex) => {
    try {
      return katex.renderToString(cleanMathBody(tex), {
        trust: false,
        strict: false,
        throwOnError: false,
        displayMode: true,
      });
    } catch {
      return match;
    }
  });

  // Bước 2b: Parse $...$ (inline mode)
  // - Cho phép '<' và '>' (toán tử so sánh trong LaTeX, vd 2<m).
  // - Chặn xuống dòng (\n) để không vô tình nuốt block.
  // - Negative lookahead chặn HTML tag THẬT (vd <p>, <br>),
  //   NHƯNG vẫn cho <em>...</em> đi qua vì cleanMathBody xử lý được.
  html = html.replace(
    /\$((?:(?!\$|\n|<(?!\/?em\b)\/?[a-zA-Z])[\s\S])+?)\$/g,
    (match, tex) => {
      try {
        return katex.renderToString(cleanMathBody(tex), {
          trust: false,
          strict: false,
          throwOnError: false,
          displayMode: false,
        });
      } catch {
        return match;
      }
    }
  );

  // Bước 2c: Parse \[...\] (display mode — LaTeX chuẩn)
  html = html.replace(/\\\[([\s\S]+?)\\\]/g, (match, tex) => {
    try {
      return katex.renderToString(cleanMathBody(tex), {
        trust: false,
        strict: false,
        throwOnError: false,
        displayMode: true,
      });
    } catch {
      return match;
    }
  });

  // Bước 2d: Parse \(...\) (inline mode — LaTeX chuẩn)
  html = html.replace(/\\\(([\s\S]+?)\\\)/g, (match, tex) => {
    try {
      return katex.renderToString(cleanMathBody(tex), {
        trust: false,
        strict: false,
        throwOnError: false,
        displayMode: false,
      });
    } catch {
      return match;
    }
  });

  return html;
}

interface Props {
  content: string;
  className?: string;
}

export default function RichTextRenderer({ content, className = "" }: Props) {
  const [cssReady, setCssReady] = useState(false);

  useEffect(() => {
    import("katex/dist/katex.min.css" as any).then(() => setCssReady(true));
  }, []);

  const html = useMemo(() => processRichText(content), [content]);

  return (
    <span
      className={`rich-text-renderer leading-relaxed ${className}`}
      style={cssReady ? undefined : { opacity: 0.01 }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
